import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, IconButton,
  Tooltip, TablePagination, Alert, Switch, Stack, Checkbox, Paper
} from '@mui/material';
import {
  Add, Search, Refresh, Edit, Delete, PhoneForwarded, AssignmentInd,
  LockOpen, CloudUpload, SwapHoriz
} from '@mui/icons-material';
import api from '../../services/api';

const empty = { number: '', number_type: 'geographic', country_code: '', carrier_id: '', monthly_fee: 0, status: 'available', inbound_target: '' };
const emptyImport = { numbers: '', carrier_id: '', tenant_id: '', number_type: 'geographic', country_code: '' };

export const NumbersPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<any>({ tenant_id: '', carrier_id: '', country: '', live: '', status: '', destination: '', ordered_for: '' });
  const [orderedForOpts, setOrderedForOpts] = useState<any[]>([]);
  useEffect(() => { api.get('/numbers/ordered-for').then(r => setOrderedForOpts(r.data || [])).catch(() => {}); }, []);
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(empty);
  const [imp, setImp] = useState<any>(emptyImport);
  const [assignTenantId, setAssignTenantId] = useState('');
  const [assignDid, setAssignDid] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [carriers, setCarriers] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [gateways, setGateways] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<any>({ total: 0, live: 0, not_live: 0 });
  // ── multi-select ──
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkDestOpen, setBulkDestOpen] = useState(false);
  const [bulkTenantId, setBulkTenantId] = useState('');
  const [bulkDest, setBulkDest] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '25', offset: String(page * 25) });
      if (search) p.set('search', search);
      Object.entries(filters).forEach(([k, v]) => {
        if (!v) return;
        if (k === 'tenant_id' && v === '__unassigned') { p.set('unassigned', 'true'); return; }
        p.set(k, String(v));
      });
      const { data: res } = await api.get(`/numbers?${p}`);
      setData(res.data || (Array.isArray(res) ? res : []));
      setTotal(res.total || 0);
      api.get('/numbers/summary').then(({ data: s }) => setSummary(s)).catch(() => {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, filters]);

  const countryOf = (num: string) => {
    const n = (num || '').replace(/[^\d+]/g, '');
    if (n.startsWith('+44') || n.startsWith('0')) return 'UK';
    if (n.startsWith('+1')) return 'USA';
    return '—';
  };
  const setFilter = (k: string, v: string) => { setFilters((f: any) => ({ ...f, [k]: v })); setPage(0); };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/carriers?limit=200').then(({ data: r }) => setCarriers(r.data || [])).catch(() => {});
    api.get('/tenants?limit=200').then(({ data: r }) => setTenants((r.data || []).filter((t: any) => t.tier !== 'provider'))).catch(() => {});
    api.get('/numbers/delivery-gateways').then(({ data: r }) => setGateways(r || {})).catch(() => {});
  }, []);

  const tenantName = (id: string) => tenants.find(t => t.id === id)?.name || (id ? id.slice(0, 8) + '…' : '—');
  const carrierName = (id: string) => carriers.find(c => c.id === id)?.name || id || '—';
  const effectiveDest = (row: any) => row.inbound_target || (row.tenant_id ? gateways[row.tenant_id] : '') || '';

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (editing) await api.patch(`/numbers/${editing.id}`, form);
      else await api.post('/numbers', form);
      setDialog(false); load();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const doImport = async () => {
    setSaving(true); setError('');
    try {
      const { data: r } = await api.post('/numbers/import', imp);
      setImportDialog(false);
      setNotice(`Imported ${r.created} DID(s)${r.skipped?.length ? `, skipped ${r.skipped.length} already present` : ''}. All start inactive (503) until you Go Live.`);
      load();
    } catch (e: any) { setError(e.response?.data?.message || 'Import failed'); }
    finally { setSaving(false); }
  };

  const doAssign = async () => {
    setSaving(true); setError('');
    try {
      await api.patch(`/numbers/${assignDid.id}/assign`, { tenant_id: assignTenantId });
      setAssignDialog(false);
      setNotice(`${assignDid.number} assigned to ${tenantName(assignTenantId)}. It is inactive (503) — Go Live when ready to cut over.`);
      load();
    } catch (e: any) { setError(e.response?.data?.message || 'Assign failed'); }
    finally { setSaving(false); }
  };

  const release = async (row: any) => {
    if (!window.confirm(`Release ${row.number} back to inventory? It will return 503 (traffic to old switch).`)) return;
    await api.patch(`/numbers/${row.id}/release`); setNotice(`${row.number} released.`); load();
  };

  const toggleLive = async (row: any) => {
    setError(''); setNotice('');
    try {
      if (row.is_live) {
        if (!window.confirm(`Take ${row.number} down? esbc2 will return 503 and BT will send it to the old switch.`)) return;
        const { data: r } = await api.post(`/numbers/${row.id}/take-down`);
        setNotice(r.message);
      } else {
        const { data: r } = await api.post(`/numbers/${row.id}/go-live`);
        setNotice(r.message);
      }
      load();
    } catch (e: any) { setNotice(''); setError(e.response?.data?.message || 'Action failed'); }
  };

  // ── multi-select handlers ──
  const selIds = () => Array.from(selected);
  const toggleSel = (id: string) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pageIds = data.map(r => r.id);
  const allPageSel = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const somePageSel = pageIds.some(id => selected.has(id)) && !allPageSel;
  const toggleSelAllPage = () => setSelected(s => {
    const n = new Set(s);
    if (allPageSel) pageIds.forEach(id => n.delete(id)); else pageIds.forEach(id => n.add(id));
    return n;
  });
  const selectAllMatching = async () => {
    const p = new URLSearchParams({ limit: '100000', offset: '0' });
    if (search) p.set('search', search);
    Object.entries(filters).forEach(([k, v]) => {
      if (!v) return;
      if (k === 'tenant_id' && v === '__unassigned') { p.set('unassigned', 'true'); return; }
      p.set(k, String(v));
    });
    const { data: res } = await api.get(`/numbers?${p}`);
    setSelected(new Set((res.data || []).map((r: any) => r.id)));
  };
  const bulkCall = async (fn: () => Promise<any>, confirmMsg?: string) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setError(''); setNotice(''); setSaving(true);
    try {
      const { data: r } = await fn();
      setNotice(r.message || 'Done'); setSelected(new Set());
      setBulkAssignOpen(false); setBulkDestOpen(false);
      load();
    } catch (e: any) { setError(e.response?.data?.message || 'Bulk action failed'); }
    finally { setSaving(false); }
  };
  const doBulkAssign = () => bulkCall(() => api.post('/numbers/bulk/assign', { ids: selIds(), tenant_id: bulkTenantId }));
  const doBulkDest = () => bulkCall(() => api.patch('/numbers/bulk/destination', { ids: selIds(), inbound_target: bulkDest }));
  const doBulkGoLive = () => bulkCall(() => api.post('/numbers/bulk/go-live', { ids: selIds() }), `Go LIVE ${selected.size} DID(s) on esbc2?`);
  const doBulkTakeDown = () => bulkCall(() => api.post('/numbers/bulk/take-down', { ids: selIds() }), `Take ${selected.size} DID(s) down → 503 (traffic to old switch)?`);
  const doBulkRelease = () => bulkCall(() => api.post('/numbers/bulk/release', { ids: selIds() }), `Release ${selected.size} DID(s) to inventory?`);

  const STATUS_COLOR: any = { available: 'success', assigned: 'primary', suspended: 'warning', porting: 'info', released: 'default' };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PhoneForwarded sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>DID Numbers</Typography>
            <Typography variant="body2" color="text.secondary">
              {summary.total} in inventory · <b style={{ color: '#2e7d32' }}>{summary.live} live on esbc2</b> · {summary.not_live} inactive (503 → old switch)
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" placeholder="Search numbers..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 200 }} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => { setImp({ ...emptyImport }); setError(''); setImportDialog(true); }}>Import</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({ ...empty }); setError(''); setDialog(true); }}>Add</Button>
        </Box>
      </Box>

      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {error && !dialog && !assignDialog && !importDialog && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        <TextField select size="small" label="Customer" value={filters.tenant_id} onChange={e => setFilter('tenant_id', e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All customers</MenuItem>
          <MenuItem value="__unassigned"><em>Unassigned</em></MenuItem>
          {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Carrier" value={filters.carrier_id} onChange={e => setFilter('carrier_id', e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All carriers</MenuItem>
          {carriers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Country" value={filters.country} onChange={e => setFilter('country', e.target.value)} sx={{ minWidth: 120 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="UK">UK</MenuItem>
          <MenuItem value="USA">USA</MenuItem>
        </TextField>
        <TextField select size="small" label="Live on esbc2" value={filters.live} onChange={e => setFilter('live', e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Live</MenuItem>
          <MenuItem value="false">Not live (503)</MenuItem>
        </TextField>
        <TextField select size="small" label="Status" value={filters.status} onChange={e => setFilter('status', e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          {['available', 'assigned', 'suspended', 'porting', 'released'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Destination" value={filters.destination} onChange={e => setFilter('destination', e.target.value)} placeholder="host / aor" sx={{ minWidth: 150 }} />
        <TextField select size="small" label="Ordered for" value={filters.ordered_for} onChange={e => setFilter('ordered_for', e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">All</MenuItem>
          {orderedForOpts.map((o: any) => <MenuItem key={o.value} value={o.value}>{o.value} ({o.total})</MenuItem>)}
        </TextField>
        {(filters.tenant_id || filters.carrier_id || filters.country || filters.live || filters.status || filters.destination) &&
          <Button size="small" onClick={() => { setFilters({ tenant_id: '', carrier_id: '', country: '', live: '', status: '', destination: '' }); setPage(0); }}>Clear</Button>}
      </Box>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <Paper variant="outlined" sx={{ p: 1, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', bgcolor: 'action.hover' }}>
          <Chip label={`${selected.size} selected`} color="primary" size="small" />
          {allPageSel && total > data.length && (
            <Button size="small" onClick={selectAllMatching}>Select all {total} matching filter</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<AssignmentInd />} onClick={() => { setBulkTenantId(''); setError(''); setBulkAssignOpen(true); }}>Assign to customer</Button>
          <Button size="small" variant="outlined" startIcon={<SwapHoriz />} onClick={() => { setBulkDest(''); setError(''); setBulkDestOpen(true); }}>Set destination</Button>
          <Button size="small" variant="outlined" color="success" onClick={doBulkGoLive}>Go Live</Button>
          <Button size="small" variant="outlined" color="warning" onClick={doBulkTakeDown}>Take Down (503)</Button>
          <Button size="small" variant="outlined" color="warning" startIcon={<LockOpen />} onClick={doBulkRelease}>Release</Button>
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Paper>
      )}

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={allPageSel} indeterminate={somePageSel} onChange={toggleSelAllPage} />
                  </TableCell>
                  {['DID Number', 'Country', 'Area', 'Carrier', 'Ordered for', 'Customer (using)', 'Destination', 'Live on esbc2', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(row => {
                  const dest = effectiveDest(row);
                  return (
                    <TableRow key={row.id} hover selected={selected.has(row.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(row.id)} onChange={() => toggleSel(row.id)} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.number}</TableCell>
                      <TableCell><Chip label={row.country_code || countryOf(row.number)} size="small" variant="outlined" /></TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{row.area_name ? <>{row.area_name} <span style={{ color: '#999' }}>({row.area_code})</span></> : <em style={{ color: '#bbb' }}>—</em>}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{carrierName(row.carrier_id)}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{row.ordered_for ? <Tooltip title={row.order_ref ? `Order ${row.order_ref}` : ''}><span>{row.ordered_for}</span></Tooltip> : <em style={{ color: '#bbb' }}>—</em>}</TableCell>
                      <TableCell>{row.tenant_id ? tenantName(row.tenant_id) : <em style={{ color: '#999' }}>unassigned</em>}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: dest ? 'text.primary' : 'error.main' }}>
                        {dest || 'no destination'}{row.inbound_target && <Chip label="override" size="small" sx={{ ml: 0.5, height: 16, fontSize: 9 }} />}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Switch size="small" checked={!!row.is_live}
                            disabled={!row.tenant_id || (!dest && !row.is_live)}
                            onChange={() => toggleLive(row)} color="success" />
                          {row.is_live
                            ? <Chip label="LIVE" size="small" color="success" sx={{ height: 20 }} />
                            : <Chip label="503 → old" size="small" variant="outlined" sx={{ height: 20 }} />}
                        </Stack>
                      </TableCell>
                      <TableCell><Chip label={row.status} size="small" color={STATUS_COLOR[row.status] || 'default'} /></TableCell>
                      <TableCell>
                        <Tooltip title="Edit / destination override"><IconButton size="small" onClick={() => {
                          setEditing(row);
                          setForm({ number: row.number, number_type: row.number_type, country_code: row.country_code || '', carrier_id: row.carrier_id || '', monthly_fee: row.monthly_fee, status: row.status, inbound_target: row.inbound_target || '', ordered_for: row.ordered_for || '', order_ref: row.order_ref || '' });
                          setError(''); setDialog(true);
                        }}><Edit fontSize="small" /></IconButton></Tooltip>
                        {row.tenant_id
                          ? <Tooltip title="Change customer (drops live)"><IconButton size="small" color="primary" onClick={() => { setAssignDid(row); setAssignTenantId(''); setError(''); setAssignDialog(true); }}><SwapHoriz fontSize="small" /></IconButton></Tooltip>
                          : <Tooltip title="Assign to customer"><IconButton size="small" color="primary" onClick={() => { setAssignDid(row); setAssignTenantId(''); setError(''); setAssignDialog(true); }}><AssignmentInd fontSize="small" /></IconButton></Tooltip>}
                        {row.tenant_id && (
                          <Tooltip title="Release to inventory"><IconButton size="small" color="warning" onClick={() => release(row)}><LockOpen fontSize="small" /></IconButton></Tooltip>
                        )}
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={async () => {
                          if (window.confirm(`Delete ${row.number}?`)) { await api.delete(`/numbers/${row.id}`); load(); }
                        }}><Delete fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No DID numbers match. Adjust filters, or Import a BT range / Add a single number.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={25}
              onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[25]} />
          </>
        )}
      </Card>

      {/* Add / Edit */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit DID Number' : 'Add DID Number'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="DID Number *" value={form.number}
                onChange={e => setForm({ ...form, number: e.target.value })}
                placeholder="e.g. +442071234567" disabled={!!editing} required />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Number Type" value={form.number_type}
                onChange={e => setForm({ ...form, number_type: e.target.value })}>
                {['geographic', 'mobile', 'tollfree', 'premium', 'shortcode'].map(t => (
                  <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Country Code" value={form.country_code}
                onChange={e => setForm({ ...form, country_code: e.target.value })} placeholder="e.g. 1, 44, 971" />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Carrier (issuer)" value={form.carrier_id || ''}
                onChange={e => setForm({ ...form, carrier_id: e.target.value })}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {carriers.filter(c => c.status === 'active').map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Inbound destination override" value={form.inbound_target}
                onChange={e => setForm({ ...form, inbound_target: e.target.value })}
                placeholder="host:port (blank = use customer's delivery gateway)"
                helperText="Leave blank to deliver to the assigned customer's gateway." />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Ordered for (end customer on carrier order)" value={form.ordered_for || ''}
                onChange={e => setForm({ ...form, ordered_for: e.target.value })} placeholder="e.g. HESAP TECH LIMITED" sx={{ mb: 2 }} />
              <TextField fullWidth label="Carrier order reference" value={form.order_ref || ''}
                onChange={e => setForm({ ...form, order_ref: e.target.value })} placeholder="e.g. GSIPO-00094" sx={{ mb: 2 }} />
              <TextField fullWidth type="number" label="Monthly Fee (USD)" value={form.monthly_fee}
                onChange={e => setForm({ ...form, monthly_fee: parseFloat(e.target.value) || 0 })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.number}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save Changes' : 'Add Number'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import DIDs</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={6} label="DID numbers *" value={imp.numbers}
                onChange={e => setImp({ ...imp, numbers: e.target.value })}
                placeholder={'One per line (or comma/space separated)\n+442071234500\n+442071234501\n+442071234502'} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Carrier (issuer)" value={imp.carrier_id}
                onChange={e => setImp({ ...imp, carrier_id: e.target.value })}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {carriers.filter(c => c.status === 'active').map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Assign to customer (optional)" value={imp.tenant_id}
                onChange={e => setImp({ ...imp, tenant_id: e.target.value })}>
                <MenuItem value=""><em>Leave in inventory</em></MenuItem>
                {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Number Type" value={imp.number_type}
                onChange={e => setImp({ ...imp, number_type: e.target.value })}>
                {['geographic', 'mobile', 'tollfree', 'premium', 'shortcode'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Country Code" value={imp.country_code}
                onChange={e => setImp({ ...imp, country_code: e.target.value })} placeholder="e.g. 44" />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">Imported DIDs are <b>inactive (503 → old switch)</b> until you Go Live on each. Duplicates already in inventory are skipped.</Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setImportDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={doImport} disabled={saving || !imp.numbers.trim()}>
            {saving ? <CircularProgress size={20} /> : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign / Change customer */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{assignDid?.tenant_id ? 'Change customer' : 'Assign'} — {assignDid?.number}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {assignDid?.tenant_id && <Alert severity="warning" sx={{ mb: 2 }}>Changing the customer will drop this DID to <b>503</b>. Go Live again once the new owner is ready.</Alert>}
          <TextField select fullWidth label="Customer *" value={assignTenantId}
            onChange={e => setAssignTenantId(e.target.value)}>
            <MenuItem value=""><em>Select customer...</em></MenuItem>
            {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={doAssign} disabled={saving || !assignTenantId}>
            {saving ? <CircularProgress size={20} /> : assignDid?.tenant_id ? 'Change' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk: Assign selected to a customer */}
      <Dialog open={bulkAssignOpen} onClose={() => setBulkAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign {selected.size} DID(s) to a customer</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>Each DID gets the customer's delivery destination and is dropped to <b>503</b> until you Go Live.</Alert>
          <TextField select fullWidth label="Customer *" value={bulkTenantId} onChange={e => setBulkTenantId(e.target.value)}>
            <MenuItem value=""><em>Select customer...</em></MenuItem>
            {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBulkAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doBulkAssign} disabled={saving || !bulkTenantId}>
            {saving ? <CircularProgress size={20} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk: Set destination on selected */}
      <Dialog open={bulkDestOpen} onClose={() => setBulkDestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set destination on {selected.size} DID(s)</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField fullWidth label="Inbound target" value={bulkDest} onChange={e => setBulkDest(e.target.value)}
            placeholder="host:port;transport  (blank = customer default)" sx={{ fontFamily: 'monospace' }} />
          <Alert severity="info" sx={{ mt: 2 }}>Leave blank to clear the per-DID override and fall back to the customer's delivery gateway.</Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBulkDestOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doBulkDest} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
